import { forwardRef, type KeyboardEvent, type MouseEvent } from 'react';
import { DefaultLinkComponent } from '@pkgs/components/providers/AppContextProvider';
import { panic } from '@ev-tech/search-modules-api';

import { UAExclusiveSearchResultCard } from './UAExclusiveSearchResultCard';
import { SearchResultCardProvider } from './SearchResultCardProvider';
import {
  type DefaultSearchResultCardProps,
  type RestrictedSearchResultCardProps,
  type SearchResultsCardProps,
  type UAExclusiveSearchResultCardProps,
} from './SearchResultCard.types';
import { RestrictedSearchResultCard } from './RestrictedSearchResultCard';
import { DefaultSearchResultCard } from './DefaultSearchResultCard';
import { DefaultImageComponent } from '../Image/DefaultImageComponent';

export type {
  Attribute,
  PositivePreferenceMatch,
  PreferencesObject,
  SearchResultsCardProps,
  SearchResultsCardVariant,
} from './SearchResultCard.types';

export const SearchResultCard = forwardRef<HTMLElement, SearchResultsCardProps>(
  ({ listingResult, style, openUserAccountModal, ...searchResultCardProps }: SearchResultsCardProps, ref) => {
    const { listing, restricted, userAccountExclusive } = listingResult;

    const defaultedCardProps = {
      ...searchResultCardProps,
      articleRef: ref,
      headlineAs: searchResultCardProps.headlineAs ?? 'h2',
      imageComponent: searchResultCardProps.imageComponent ?? DefaultImageComponent,
      isApproximateLocation: searchResultCardProps.isApproximateLocation ?? false,
      linkComponent: searchResultCardProps.linkComponent ?? DefaultLinkComponent,
      shouldHideImage: searchResultCardProps.shouldHideImage ?? false,
      variant: searchResultCardProps.variant ?? 'default',
      style,
    };

    if (listing) {
      const cardProps = {
        ...defaultedCardProps,
        cardType: 'default',
        listing,
      } satisfies DefaultSearchResultCardProps;

      return (
        <SearchResultCardProvider cardProps={cardProps}>
          <DefaultSearchResultCard key={listing.id} style={style} />
        </SearchResultCardProvider>
      );
    }

    if (userAccountExclusive) {
      const cardProps = {
        ...defaultedCardProps,
        cardType: 'uaExclusive',
        listing: userAccountExclusive,
        openUserAccountModal,
        onClick: (e: MouseEvent<HTMLElement>) => {
          e.preventDefault();
          e.stopPropagation();
          openUserAccountModal?.();
        },
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;

          e.preventDefault();
          e.stopPropagation();
          openUserAccountModal?.();
        },
      } satisfies UAExclusiveSearchResultCardProps;

      return (
        <SearchResultCardProvider cardProps={cardProps}>
          <UAExclusiveSearchResultCard key={userAccountExclusive.id} style={style} />
        </SearchResultCardProvider>
      );
    }

    // impossible to happen, but to satisfy typescript
    if (!restricted) return panic('SearchListingResult has no listing, restricted, or userAccountExclusive');

    const cardProps = {
      ...defaultedCardProps,
      cardType: 'restricted',
      listing: restricted,
    } satisfies RestrictedSearchResultCardProps;

    return (
      <SearchResultCardProvider cardProps={cardProps}>
        <RestrictedSearchResultCard key={restricted.id} style={style} />
      </SearchResultCardProvider>
    );
  },
);

SearchResultCard.displayName = 'SearchResultCard';
